import { useEffect } from "react"
import type { UseFormReturn } from "react-hook-form"

import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"

type RadioFieldProps = {
  form: UseFormReturn<any>
  name: string
  label?: string
  description?: string
  options: string[]
  layout?: "inline" | "stacked"
  allowBlank?: boolean
  defaultValue?: string | null
}

export const RadioField = ({
  form,
  name,
  label,
  description,
  options,
  layout = "inline",
  allowBlank = false,
  defaultValue
}: RadioFieldProps) => {
  const watchedValue = form.watch(name)

  useEffect(() => {
    if (defaultValue != null && (watchedValue == null || watchedValue === "")) {
      form.setValue(name, defaultValue, { shouldDirty: false })
    }
  }, [defaultValue, form, name, watchedValue])

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const value = field.value ?? ""

        const handleChange = (next: string) => {
          if (allowBlank && field.value === next) {
            field.onChange("")
            return
          }
          field.onChange(next)
        }

        return (
          <FormItem id={`field-${name}`} data-field={name} className="space-y-2">
            {label && <FormLabel className="text-sm font-medium text-foreground">{label}</FormLabel>}
            {description ? <FormDescription>{description}</FormDescription> : null}
            <RadioGroup
              value={value}
              onValueChange={handleChange}
              className={layout === "inline" ? "flex flex-wrap gap-4" : "grid gap-2"}
              onBlur={field.onBlur}
            >
              {options.map(option => {
                const id = `${field.name}-${option}`
                return (
                  <label key={option} htmlFor={id} className="flex items-center gap-2 text-sm text-foreground">
                    <RadioGroupItem id={id} value={option} />
                    <span>{option}</span>
                  </label>
                )
              })}
            </RadioGroup>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
